import { IUser } from '../auth/auth';
import { IPartnerData } from '../partner/partner';

export enum IPostAttachmentTypes {
  IMAGE = 'image',
  MODEL = 'model',
  YOUTUBE = 'youtube',
  ZIP = 'zip',
}

export enum IAttachmentCategory {
  postMedia = 'postMedia',
  postModel = 'postModel',
  postThumbnail = 'postThumbnail',
  postZip = 'postZip',
}

export enum ILicense {
  CreativeCommonsPublicDomain = 'Creative Commons Public Domain',
  CreativeCommonsAttribution = 'Creative Commons Attribution',
  CreativeCommonsAttributionShareAlike = 'Creative Commons Attribution - Share Alike',
  CreativeCommonsAttributionNoDerivatives = 'Creative Commons Attribution - No Derivatives',
  CreativeCommonsAttributionNoncommercial = 'Creative Commons Attribution - Noncommercial',
  CreativeCommonsAttributionNoncommercialShareAlike = 'Creative Commons Attribution - Noncommercial - Share Alike',
  CreativeCommonsAttributionNoncommercialNoDerivatives = 'Creative Commons Attribution - Noncommercial - No Derivatives',
}

export interface IPostAttachment {
  _id: string;
  user: string;
  parentId: string;
  parentType: string;
  resourcePath: string;
  resourceType: IPostAttachmentTypes;
  category: IAttachmentCategory;
  fileName: string;
  fileSize: number;
  url: string;
  downloadCount: number;
  createdAt: string;
}

export interface IPostCategory {
  _id: string;
  name: string;
  type: string;
  createdAt: string;
}

export interface IPostSubCategory {
  _id: string;
  name: string;
  createdAt: string;
}

export type IPostTag = {
  _id: string;
  name: string;
  type: string;
  createdBy: string;
  createdAt: string;
};

export enum IPostVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  UNLISTED = 'unlisted',
  DRAFT = 'draft',
}

export enum IBuiltWith {
  ONEFINITY = 'Onefinity CNC',
  OTHERS = 'Others',
}

export enum IOnefinityModel {
  ELITE_SERIES = 'Onefinity Elite Series',
  PRO_SERIES = 'Onefinity Pro Series',
  ORIGINAL_SERIES = 'Onefinity Original Series',
}

export enum IOnefinityController {
  MASSO = 'Masso',
  BB = 'Build Botics',
}

export enum IOnefinitySize {
  FOREMAN_48X48 = 'Foreman 48x48',
  JOURNEYMAN_48X32 = 'Journeyman 48x32',
  WOODWORKER_32X32 = 'Woodworker 32x32',
  MACHINIST_16X16 = 'Machinist 16x16',
}

export enum ISoftwareUsed {
  CARVECO = 'Carveco',
  VECTRIC = 'Vectric',
  FUSION_360 = 'Fusion 360',
  CARBIDE_CREATE = 'Carbide Create',
  EASEL = 'Easel',
  SOLIDWORKS = 'Solidworks',
  MESHCAM = 'MeshCam',
  EZ_CAD = 'EZ Cad',
  FREECAM = 'FreeCAM',
  ESTLCAM = 'Estlcam',
  OTHER = 'Other',
}

export enum IWood {
  WOOD_SOFT = 'Wood (soft)',
  WOOD_HARD = 'Wood (hard)',
}
export enum IMetals {
  ALUMINUM = 'Aluminum',
  BRASS = 'Brass',
  COPPER = 'Copper',
}
export enum IMDF {
  PLASTIC = 'Plastic',
  ACRYLIC = 'Acrylic',
  FOAM = 'Foam',
}
export enum IEVA {
  LEATHER = 'Leather',
  GLASS = 'Glass',
}

export enum IMaterialUsed {
  WOOD = IWood,
  METALS = IMetals,
  MDF = IMDF,
  EVA = IEVA,
}

export enum IJobType {
  SINGLE_SIDED = 'Single Sided',
  DOUBLE_SIDED = 'Double Sided',
  ROTARY = 'Rotary',
}

export enum IUnits {
  INCHES = 'Inches',
  MM = 'MM',
}

export enum IZZeroPosition {
  MATERIAL_SURFACE = 'Material Surface',
  MACHINE_BED = 'Machine Bed',
}

export enum IXYDatumPosition {
  LOWER_LEFT = 'Lower Left',
  CENTER = 'Center',
  UPPER_LEFT = 'Upper Left',
  LOWER_RIGHT = 'Lower Right',
  UPPER_RIGHT = 'Upper Right',
}

export enum ILaserOrEndmill {
  ENDMILL = 'Endmill',
  LASER = 'Laser',
}

export enum IBitType {
  UPCUT_ENDMILL = 'Upcut Endmill',
  DOWNCUT_ENDMILL = 'Downcut Endmill',
  V_BIT = 'V Bit',
  TAPERED_BALL_NOSE = 'Tapered Ball Nose',
  BALL_NOSE = 'Ball Nose',
  SURFACING = 'Surfacing',
  BOWL = 'Bowl',
  DRAG_BIT = 'Drag Bit',
  OGEE_ROUNDOVER = 'Ogee / Roundover',
  DRILL_MILL = 'Drill Mill',
  DOVETAIL = 'Dovetail',
}

export enum IBitUsedUnit {
  INCHES = 'Inches',
  MM = 'MM',
}

export enum IToolSetter {
  ENABLED = 'Enabled',
  DISABLE = 'Disable',
}

export enum IProbeForZHeightCommand {
  NO = 'No',
  YES = 'Yes',
}

export type IToolData = {
  modelId: string;
  toolUsed?: number;
  toolUnit?: IBitUsedUnit;
  toolType?: IBitType;
  laserOrEndmill: ILaserOrEndmill;
  _id: string;
};

export type IJobSize = {
  width: number;
  height: number;
  thickness: number;
  diameter: number;
  length: number;
};

export interface IModel {
  id: string;
  modelURL: string;
  modelFile: File;
}

export type IPost = {
  _id: string;
  title: string;
  description: string;
  user: string;
  category: IPostCategory;
  models: IModel[];
  subCategory: IPostSubCategory | string;
  tags: IPostTag[] | [];
  visibility: IPostVisibility;
  onefinityModel: IOnefinityModel;
  onefinityController: IOnefinityController;
  onefinitySize: IOnefinitySize;

  softwareUsed: ISoftwareUsed;
  materialUsed: IMaterialUsed;

  jobType: IJobType;
  jobSize: {
    width: number;
    height: number;
    thickness: number;
  };
  units: IUnits;
  zZeroPosition: IZZeroPosition;
  xyDatumPosition: IXYDatumPosition;
  laserOrEndmill: ILaserOrEndmill;

  toolData: IToolData[];

  toolSetter: IToolSetter;
  probeForZHeightCommand: IProbeForZHeightCommand;
  spindleSpeed: number;
  createdAt: string;
  likes: number;
  comments: number;
  hasLikedByUser?: boolean;

  partner?: IPartnerData;
};

export interface IPostWithAttachments extends IPost {
  attachments: IPostAttachment[];
}

export interface IPostWithUser extends IPost {
  user: IUser;
}

export interface IPostWithUserAndAttachments extends IPost {
  user: IUser;
  attachments: IPostAttachment[];
}

export interface IIPostWithUserAndAttachmentsRequestParams {
  category?: string;
  subCategory?: string;
  postedAt?: string;
  author?: string;
  page?: number;
  limit?: number;
  toolType?: string;
  searchQuery?: string;
  partner?: string;
}

export type EnumType = Record<string, string>;
export type GroupedEnum = Record<string, EnumType>;

export interface IOption {
  value: string;
  label: string;
}

export interface IGroupedOption {
  label: string;
  options: Option[];
}

export interface IPostDraft {
  user: string;
  tags: string[];
  category: string;
  title: string;
  description: string;
  thumbnail: File | null | string | IPostAttachment;
  visibility: IPostVisibility;
  _id: string;
}

export type IPostDetailResponse = {
  _id: string;
  title: string;
  description: string;
  user: IUser;
  category: IPostCategory;
  tags: IPostTag[];
  visibility: string;
  onefinityModel: string;
  onefinityController: string;
  onefinitySize: string;
  softwareUsed: string;
  materialUsed: string;
  jobType: string;
  jobSize: IJobSize;
  units: string;
  zZeroPosition: string;
  xyDatumPosition: string;
  laserOrEndmill: string;
  toolSetter: string;
  probeForZHeightCommand: string;
  spindleSpeed: number;
  createdAt: string;
  toolData: IToolData[];
  likes: number;
  hasLikedByUser: boolean;
  comments: number;
  currentPage: number;
  builtWith: IBuiltWith;
  attachments: IPostAttachment[];
  originalMachine: string;
  supportedMachines: string[];
  licenceType: string;
};

export type IPostUploadData = {
  title: string;
  description: string;
  category: string;
  tags: string[];
  onefinityModel: IOnefinityModel | '';
  onefinityController: IOnefinityController | '';
  onefinitySize: IOnefinitySize | '';
  softwareUsed: ISoftwareUsed;
  materialUsed: IMaterialUsed;
  originalMachine: string;
  supportedMachines: string[];
  jobType: IJobType;
  units: IUnits;
  jobSizeHeight: number;
  jobSizeThickness: number;
  jobSizeWidth: number;
  jobSizeDiameter: number;
  jobSizeLength: number;
  zZeroPosition: IZZeroPosition;
  xyDatumPosition: IXYDatumPosition;
  laserOrEndmill: ILaserOrEndmill;
  toolData: IToolData[];
  toolSetter: IToolSetter;
  probeForZHeightCommand: IProbeForZHeightCommand;
  spindleSpeed: number;
  models: IModel[];
  mediaFiles: File[];
  youtubeAttachments: string[];
  licenceType: ILicense;
  thumbnail: (File | null) | (IPostAttachment | undefined);
};

export type IPostAttachmentUploadRequest = {
  thumbnail?: File;
  modelFiles?: File[];
  mediaFiles?: File[];
};

export type IPostDataUploadRequest = {
  title: string;
  description: string;
  category: string;
  tags: string[];
  onefinityModel: string;
  onefinityController: string;
  onefinitySize: string;
  softwareUsed: string;
  materialUsed: string;
  jobType: string;
  jobSizeHeight: number;
  jobSizeThickness: number;
  jobSizeWidth: number;
  jobSizeDiameter: number;
  jobSizeLength: number;
  units: string;
  zZeroPosition: string;
  xyDatumPosition: string;
  laserOrEndmill: string;
  toolSetter: string;
  probeForZHeightCommand: string;
  spindleSpeed: number;
  toolData: IToolData[];
  currentPage: number;
  builtWith: IBuiltWith | '';
  // user: IUser;
  // visibility: string;

  // createdAt: string;
  // toolData: IToolData[];
  // jobSize: IJobSize;
  // likes: number;
  // hasLikedByUser: boolean;
  // comments: number;
  // attachments: IPostAttachment[];
};

export interface IPostAutoComplete {
  _id: string;
  title: string;
}
